import React, { useEffect, useRef, useState } from "react"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Card,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material"
import ClearIcon from "@mui/icons-material/Clear"
import Grid from "@mui/material/Grid2"
import SingleSelect from "@/components/Select/SingleSelect"
import BillingDetails from "./BillingDetails"
import SecondaryButton from "../Buttons/SecondaryButton"
import usePurchaseProductable from "@/hooks/usePurchaseProductable"
import { z } from "zod"
import CustomAutoComplete from "../Select/CustomAutoComplete"
import BarcodeInput from "../BarcodeInput"
import { toast } from "sonner"
import { apiFetch } from "@/hooks/apiFetch"

export const ProductableSchema = z.object({
  id: z.number(),
  name: z.string().min(3),
  code: z.string().min(1),
  quantity: z.number(),
  price: z.number(),
  discount: z.number().default(0),
})

const PurchaseProductable = ({
  controller,
  control,
  errors,
  onClose,
  name,
  helperText,
  label = "Products",
  isSubmitting,
  defaultProducts = [],
}) => {
  const Controller = controller

  const [productSearchQuery, setProductSearchQuery] = useState("")

  const {
    products,
    purchaseItems,
    subTotal,
    vatAmt,
    grandTotal,
    discountAmount,
    discount,
    isLoading: productLoading,
    rowTotal,
    handleAddedPurchaseItem,
    handleRemovePurchaseItem,
    handleQuantityChange,
    handleDiscountChange,
    handleItemDiscountChange,
    handlePriceChange,
    handleReset,
    handleAddedPurchaseItemViaBarcode,
    totalQuantity,
    totalDistinctProducts,
  } = usePurchaseProductable(defaultProducts, productSearchQuery)

  const [productResponse, setProductResponse] = useState(null)
  const [isSuccess, setIsSuccess] = useState(false)

  const fetchProduct = async (barcode) => {
    try {
      const response = await apiFetch("product/sku", {
        method: "POST",
        body: JSON.stringify({ sku: barcode }),
      })

      if (response?.data) {
        handleAddedPurchaseItemViaBarcode(response.data)
      }

      setProductResponse(response)
    } catch (error) {
      toast.error("No product found.")
      setProductResponse(null)
    }
  }

  return (
    <div>
      <Typography
        id="modal-modal-title"
        variant="h6"
        sx={{
          fontWeight: "500",
          fontSize: { xs: "12px", md: "16px" },
          marginBottom: 2,
          marginTop: 1,
        }}
        className="text-black"
      >
        {label}
      </Typography>
      <div className="w-full flex flex-col md:flex-row md:items-end md:gap-4 space-y-4 md:space-y-0 mb-4">
        <div className="w-full md:w-5/12">
          <FormControl fullWidth>
            <CustomAutoComplete
              label="Product"
              loading={productLoading}
              options={products}
              error={!!errors[name]}
              helperText={errors[name]?.message}
              optionLabel="sku"
              optionValue="id"
              onChange={(value) => {
                handleAddedPurchaseItem(value)
              }}
              onInputChange={(searchText, value, type) => {
                if (type == "input") {
                  setProductSearchQuery(searchText)
                }
              }}
            />
          </FormControl>
        </div>

        <div className="w-full md:w-5/12">
          <FormControl fullWidth>
            <BarcodeInput
              onScan={(value) => {
                if (value) {
                  fetchProduct(value)
                  console.log("value is value", value)
                }
              }}
            />
          </FormControl>
        </div>
        <div className="w-full md:w-1/12 h-full">
          <div className="border border-gray-800 rounded-md h-full w-full flex flex-col items-center">
            <div>Products</div>
            <strong>{totalDistinctProducts}</strong>
          </div>
        </div>

        <div className="w-full md:w-1/12 h-full">
          <div className="border border-gray-800 rounded-md h-full w-full flex flex-col items-center">
            <div>Quantity</div>
            <strong>{totalQuantity}</strong>
          </div>
        </div>
      </div>

      <Grid container spacing={2} sx={{ mt: 1 }}>
        <Grid item xs={12} md={9}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              borderRadius: "7px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
            className="rmui-table border"
          >
            <Table aria-label="table">
              <TableHead className="bg-f6f7f9">
                <TableRow>
                  <TableCell>S.N.</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Quantity</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Discount %</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Remove</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <Controller
                  name={name}
                  control={control}
                  defaultValue={[]}
                  render={({
                    field: { onChange: fieldOnChange, value: fieldValue },
                    fieldState: { error, isTouched, isDirty },
                  }) => (
                    <>
                      {useEffect(() => {
                        fieldOnChange(purchaseItems)
                      }, [purchaseItems])}
                      {purchaseItems.length > 0 ? (
                        purchaseItems.map((value, index) => {
                          return (
                            <TableRow key={value.id}>
                              <TableCell
                                sx={{
                                  padding: "15px 20px",
                                  fontSize: "14px",
                                  width: "30px",
                                }}
                                className="border-bottom"
                              >
                                {index + 1}
                              </TableCell>

                              <TableCell
                                sx={{
                                  padding: "15px 20px",
                                  fontSize: "14px",
                                  width: "150px",
                                }}
                                className="border-bottom"
                              >
                                <Typography
                                  variant="button"
                                  gutterBottom
                                  sx={{ display: "block" }}
                                >
                                  {value.sku}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  gutterBottom
                                  sx={{ display: "block" }}
                                >
                                  {value.code}
                                </Typography>
                              </TableCell>

                              <TableCell
                                sx={{
                                  padding: "15px 20px",
                                  fontSize: "14px",
                                  width: "100px",
                                }}
                                className="border-bottom"
                              >
                                <TextField
                                  size="small"
                                  sx={{
                                    width: "100px",
                                  }}
                                  value={value.quantity}
                                  onChange={(event) =>
                                    handleQuantityChange(
                                      index,
                                      event.target.value
                                    )
                                  }
                                  type="number"
                                  inputProps={{
                                    min: 0,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  gutterBottom
                                  sx={{
                                    display: "block",
                                    color:
                                      value.stock == 0
                                        ? "red"
                                        : value.stock < 10
                                          ? "orange"
                                          : "grey",
                                  }}
                                >
                                  Available: {value.stock}
                                </Typography>
                              </TableCell>

                              <TableCell
                                sx={{
                                  padding: "15px 20px",
                                  fontSize: "14px",
                                  width: "100px",
                                }}
                                className="border-bottom"
                              >
                                <TextField
                                  size="small"
                                  sx={{
                                    width: "100px",
                                  }}
                                  value={value.cost_price}
                                  onChange={(event) =>
                                    handlePriceChange(index, event.target.value)
                                  }
                                  type="number"
                                  inputProps={{
                                    min: 0,
                                    step: 0.01,
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: "15px 20px",
                                  fontSize: "14px",
                                  width: "100px",
                                }}
                                className="border-bottom"
                              >
                                <TextField
                                  size="small"
                                  sx={{
                                    width: "100px",
                                  }}
                                  value={value.discount}
                                  onChange={(event) =>
                                    handleItemDiscountChange(
                                      index,
                                      event.target.value
                                    )
                                  }
                                  type="number"
                                  inputProps={{
                                    min: 0,
                                  }}
                                />
                              </TableCell>
                              <TableCell
                                sx={{
                                  padding: "15px 20px",
                                  fontSize: "14px",
                                  width: "100px",
                                }}
                                className="border-bottom"
                              >
                                {rowTotal(value).toFixed(2)}
                              </TableCell>

                              <TableCell
                                sx={{
                                  padding: "15px 20px",
                                  fontSize: "14px",
                                  width: "30px",
                                }}
                                className="border-bottom"
                              >
                                <IconButton
                                  aria-label="remove"
                                  onClick={() =>
                                    handleRemovePurchaseItem(value)
                                  }
                                >
                                  <ClearIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          )
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            No items added yet.
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={3}>
          <BillingDetails
            discount={discount}
            subtotal={subTotal}
            tax={vatAmt}
            total={grandTotal}
            discountAmount={discountAmount}
            handleDiscountChange={handleDiscountChange}
          />
        </Grid>
      </Grid>
    </div>
  )
}
export default PurchaseProductable
